import { useSearchState } from './index'
import { useAcademicExpApi } from '.'
import { useSelector } from '@ggs/store'

/**
 * @typedef {import('../../definitions').CourseSectionsExpListParams} CourseSectionsExpListParams
 * @typedef {import('../../definitions').CourseSectionExpRecord} CourseSectionExpRecord
 * @typedef {import('../../definitions').SelectOption} SelectOption
 * @typedef {import('./useSearchState').SearchHandlers} SearchHandlers
 * @typedef {(values: CourseSectionsExpListParams, formik: any) => Promise<void>} OnSubmitHandler
 */

/**
 * @typedef {Object} InstructorSearchState
 * @property {CourseSectionsExpListParams} currentFilters
 * @property {number} currentPage
 * @property {string} currentPageIndex
 * @property {boolean} loading
 * @property {boolean} hasNext
 * @property {CourseSectionExpRecord[]} pageResults
 * @property {SelectOption[]} pagesIndexOptions
 * @property {number} resultsCount
 * @property {number} rowsPerPage
 */

/**
 * @typedef {Object} InstructorSearchHandlers
 * @property {OnSubmitHandler} onSubmitHandler
 */

/**
 * @typedef {Object} InstructorSearchHook
 * @property {SearchHandlers & InstructorSearchHandlers} handlers
 * @property {InstructorSearchState} state
 */

/**
 * Course search handlers and local state business logic.
 * @return {InstructorSearchHook}
 */
const useInstructorSearch = () => {
  const { getCourseSections } = useAcademicExpApi()
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)

  /**
   * @type {CourseSectionExpRecord}
   */
  const defaultSearchResults = null

  /**
   * @type {CourseSectionsExpListParams}
   */
  const defaultFilters = null
  const supportedFilters = ['instructorName', 'courseSubject']

  /**
   * Process course sections search.
   * @param {CourseSectionsExpListParams} filterParams
   */
  const searchRequestPromise = async (filterParams) => {
    const finalFilterParams = {
      ...filterParams,
      academicPeriodId: [academicPeriod],
      pageSize: 500,
    }

    return await getCourseSections(finalFilterParams)
  }

  const { handlers, state } = useSearchState({
    defaultFilters,
    supportedFilters,
    defaultSearchResults,
    searchRequestPromise,
  })

  /**
   * Handle search API request.
   * @type {OnSubmitHandler}
   * @param {CourseSectionsExpListParams} values
   * @param {any} formik
   * @return {Promise<void>}
   */
  const onSubmitHandler = async (values, { setSubmitting }) => {
    await handlers.startNewSearch(values)
    setSubmitting(false)
  }

  return {
    handlers: {
      ...handlers,
      onSubmitHandler,
    },
    state: {
      ...state,
    },
  }
}

export default useInstructorSearch
